import { useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch } from "react";
import { drawFrame, fitCell, hitTestAgent } from "../canvas/draw";
import type { ReplayEvent, ReplayState } from "../state/replay";
import type { ReplayPayload } from "../types";
import { AgentStoryPanel } from "./AgentStoryPanel";
import { CheckboxInput } from "./controls";
import { Scrubber } from "./Scrubber";

const SPEEDS = [0.5, 1, 2, 4, 8];

export interface ReplayViewProps {
  replay: ReplayPayload;
  state: ReplayState;
  dispatch: Dispatch<ReplayEvent>;
  runId: string | null;
}

export function ReplayView({ replay, state, dispatch, runId }: ReplayViewProps) {
  const host = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [cell, setCell] = useState(24);
  // The world layer (structures & items), togglable like a map layer. Default on.
  const [showWorld, setShowWorld] = useState(true);
  const hasWorld = (replay.world_components?.length ?? 0) > 0;

  // Fit the grid to the pane, tracking resizes.
  useEffect(() => {
    const element = host.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      setCell(
        fitCell(
          entry.contentRect.width,
          entry.contentRect.height,
          replay.grid.width,
          replay.grid.height,
        ),
      );
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [replay.grid.width, replay.grid.height]);

  // Redraw on tick/size changes. The bitmap is sized in device pixels (crisp on HiDPI)
  // and reallocated only when the geometry actually changes.
  useEffect(() => {
    const element = canvas.current;
    const ctx = element?.getContext("2d");
    if (!element || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const cssWidth = replay.grid.width * cell;
    const cssHeight = replay.grid.height * cell;
    if (element.width !== cssWidth * dpr || element.height !== cssHeight * dpr) {
      element.width = cssWidth * dpr;
      element.height = cssHeight * dpr;
      element.style.width = `${cssWidth}px`;
      element.style.height = `${cssHeight}px`;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawFrame(ctx, replay, state.tick - 1, cell, state.focus, showWorld);
  }, [replay, state.tick, cell, state.focus, showWorld]);

  // Escape clears focus while an agent is tracked.
  useEffect(() => {
    if (state.focus === null) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") dispatch({ type: "clearFocus" });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state.focus, dispatch]);

  const verbsSeen = useMemo(
    () => new Set(replay.frames.flatMap((f) => f.actions.map(([, verb]) => verb))),
    [replay],
  );
  // Engine convention (same as the narrator): every living agent emits exactly one
  // action per tick, so an agent absent from a frame's actions has died.
  const anyDead = useMemo(
    () => replay.frames.some((f) => f.actions.length < replay.population),
    [replay],
  );

  return (
    <div className="view">
      <div className="replay-controls">
        <button type="button" className="btn" onClick={() => dispatch({ type: "toggle" })}>
          {state.playing ? "⏸ pause" : "▶ play"}
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => dispatch({ type: "step", delta: -1 })}
        >
          −1
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => dispatch({ type: "step", delta: 1 })}
        >
          +1
        </button>
        <label className="run-bar-field">
          speed
          <select
            value={state.speed}
            onChange={(e) => dispatch({ type: "speed", speed: Number(e.target.value) })}
          >
            {SPEEDS.map((speed) => (
              <option key={speed} value={speed}>
                {speed}×
              </option>
            ))}
          </select>
        </label>
        {hasWorld && <CheckboxInput label="world" checked={showWorld} onChange={setShowWorld} />}
        <span className="tick-readout">
          tick {state.tick} / {state.maxTick}
        </span>
      </div>
      <Scrubber
        tick={state.tick}
        maxTick={state.maxTick}
        beats={replay.beats}
        onSeek={(tick) => dispatch({ type: "seek", tick })}
      />
      <div className="replay-body">
        <div className="canvas-host" ref={host}>
          <canvas
            ref={canvas}
            style={{ cursor: "pointer" }}
            onClick={(event) => {
              const rect = event.currentTarget.getBoundingClientRect();
              const hit = hitTestAgent(
                replay,
                state.tick - 1,
                cell,
                event.clientX - rect.left,
                event.clientY - rect.top,
              );
              dispatch(hit === null ? { type: "clearFocus" } : { type: "focus", agent: hit });
            }}
          />
        </div>
        {state.focus !== null && runId !== null && (
          <AgentStoryPanel
            runId={runId}
            agent={state.focus}
            replay={replay}
            tick={state.tick}
            onSeek={(tick) => dispatch({ type: "seek", tick })}
            onClear={() => dispatch({ type: "clearFocus" })}
          />
        )}
      </div>
      <div className="legend">
        {replay.verbs
          .filter((verb) => verbsSeen.has(verb))
          .map((verb) => (
            <span key={verb}>
              <span className="legend-swatch" style={{ background: replay.palette[verb] }} />
              {verb}
            </span>
          ))}
        {anyDead && (
          <span>
            <span className="legend-swatch" style={{ background: "#8b929e" }} />
            DEAD ×
          </span>
        )}
      </div>
    </div>
  );
}
